/**
 * Pure functions that label a game's state, pick a card's win, loss or
 * pending tone, and build the My picks cards in priority order (S2, S3, S6).
 *
 * No React, no clock reads: nowMs comes in. A game in progress or delayed
 * more than 18 hours after kickoff is labeled Not final, the rule events-api
 * serves (ISSUE-1148). No lock or availability re-derivation: the pick
 * tiles this reads already carry it.
 */
import { resolvePlatePair } from "../gameDetail/teamPlateText";
import type { typeBoardData, typePickTile } from "../pick/pickBoardModel";
import type { typePickSelection } from "../pick/pickSubmitModel";
import type { typeTeamName } from "../pick/useTeamNames";
import { isStaleLive } from "./staleLiveRule";
import {
  SURVIVOR_GAME_CANCELED,
  SURVIVOR_GAME_DELAYED,
  SURVIVOR_GAME_FINAL,
  SURVIVOR_GAME_LIVE,
  SURVIVOR_GAME_NOT_FINAL,
  SURVIVOR_GAME_POSTPONED,
  SURVIVOR_OUTCOME_LOST,
  SURVIVOR_OUTCOME_NOT_SAVED,
  SURVIVOR_OUTCOME_PENDING,
  SURVIVOR_OUTCOME_TIE,
  SURVIVOR_OUTCOME_WON,
} from "./survivorGameCopy";

export type typeGameStatus =
  "scheduled" | "inProgress" | "final" | "postponed" | "canceled" | "delayed";
export type typePickOutcome = "win" | "loss" | "push" | "pending";
export type typePickTone = "win" | "loss" | "pending" | "none";

export const PICK_TONE_CLASS: Record<typePickTone, string> = {
  win: "border-2 border-success bg-success-tint",
  loss: "border-2 border-error bg-error-tint",
  pending: "border-2 border-primary bg-primary-tint",
  none: "border border-hairline bg-surface",
};

export type typeMyPickCard = {
  eventId: string;
  teamId: string;
  teamName: string;
  abbreviation: string;
  logoUrl: string | null;
  opponentName: string;
  gameStateLabel: string;
  priority: number; // selection index + 1
  tone: typePickTone;
  toneLabel: string | null;
};

export function formatGameStateLabel(input: {
  status: typeGameStatus | undefined;
  kickoffAt: string;
  kickoffLabel: string;
  nowMs: number;
}): string {
  if (
    isStaleLive({
      status: input.status,
      kickoffAt: input.kickoffAt,
      nowMs: input.nowMs,
    })
  ) {
    return SURVIVOR_GAME_NOT_FINAL;
  }

  switch (input.status) {
    case "inProgress":
      return SURVIVOR_GAME_LIVE;
    case "final":
      return SURVIVOR_GAME_FINAL;
    case "postponed":
      return SURVIVOR_GAME_POSTPONED;
    case "canceled":
      return SURVIVOR_GAME_CANCELED;
    case "delayed":
      return SURVIVOR_GAME_DELAYED;
    case "scheduled":
    case undefined:
      return input.kickoffLabel;
  }
}

export function resolvePickTone(input: {
  selection: typePickSelection | null;
  savedPick: { teamId: string; outcome?: typePickOutcome | undefined } | null;
}): { tone: typePickTone; label: string | null } {
  if (input.selection === null) {
    return { tone: "none", label: null };
  }
  if (input.savedPick?.teamId !== input.selection.teamId) {
    return { tone: "pending", label: SURVIVOR_OUTCOME_NOT_SAVED };
  }
  switch (input.savedPick.outcome) {
    case "win":
      return { tone: "win", label: SURVIVOR_OUTCOME_WON };
    case "loss":
      return { tone: "loss", label: SURVIVOR_OUTCOME_LOST };
    case "push":
      return { tone: "pending", label: SURVIVOR_OUTCOME_TIE };
    case "pending":
    case undefined:
      return { tone: "pending", label: SURVIVOR_OUTCOME_PENDING };
  }
}

export function buildMyPickCards(input: {
  selections: readonly typePickSelection[];
  tiles: readonly typePickTile[];
  savedPicks: typeBoardData["myPicks"];
  gameStateLabelByEventId: Readonly<Record<string, string>>;
  teamsById: ReadonlyMap<string, typeTeamName>;
}): typeMyPickCard[] {
  const cards: typeMyPickCard[] = [];
  input.selections.forEach((selection, i) => {
    const tile = input.tiles.find((t) => t.eventId === selection.eventId);
    if (tile === undefined) return;
    const side = tile.home.teamId === selection.teamId ? tile.home : tile.away;
    const opponent =
      tile.home.teamId === selection.teamId ? tile.away : tile.home;
    const { awayText, homeText } = resolvePlatePair({
      away: {
        name: tile.away.teamName,
        abbreviation: tile.away.teamAbbreviation,
      },
      home: {
        name: tile.home.teamName,
        abbreviation: tile.home.teamAbbreviation,
      },
    });
    const savedPick =
      input.savedPicks.find((pick) => pick.eventId === selection.eventId) ??
      null;
    const { tone, label } = resolvePickTone({ selection, savedPick });
    cards.push({
      eventId: selection.eventId,
      teamId: side.teamId,
      teamName: side.teamName,
      abbreviation: tile.home.teamId === selection.teamId ? homeText : awayText,
      logoUrl: input.teamsById.get(side.teamId)?.logoUrl ?? null,
      opponentName: opponent.teamName,
      gameStateLabel: input.gameStateLabelByEventId[selection.eventId] ?? "",
      priority: i + 1,
      tone,
      toneLabel: label,
    });
  });
  return cards;
}
